/*
  F-0588: a field's blur never moves or hides what a press is landing on. A blur fires on the press
  (`pointerdown`), before the release; whatever it draws or removes there (a problem under the field,
  an added row, a closed editor) moves the control under the finger, the release lands somewhere else
  and the click is lost, on every engine. A blur inside a press waits until the press's click has run.

  I-1682 (gate 693) found it, and also why a form cannot read the press from where focus went.

  Where focus goes on a press depends on the engine. Chromium focuses a pressed button; WebKit (the
  iPhone app, the macOS desktop app, Safari) focuses no button on a click and hands focus to the
  nearest focusable ancestor, the sheet's panel. A blur that drew "Name what brings it back" at once
  also moved everything under it between the press and the release: in WebKit the release landed on
  the select above Details, and the click went to their common ancestor, so it was lost.

  The press lasts from `pointerdown` until its `click` has run, or until it is cancelled. A blur
  inside it waits for that, and hears what the click landed on.

  T-0698 (I-1681): this is the app's ONE press tracker. Its document listeners are installed at module
  load, not while a form is mounted, so the pointerdown that opens the first menu is already seen. Besides
  the press the blur helpers wait on (primary button or touch only), it records what a menu needs: which
  press is down (a token, any button), which was released last, the pointer type that reached the page
  last (touch, mouse or a key), and a hook that runs before the next click reaches anything else. The
  menu keyboard (`useMenuKeyboard`) used to keep its own document listeners for these.

  F-0604 WAIT-21a: it also stamps when the last press was (a pointer down, or Enter or Space), which a
  wait reads to count its stages from the press that started it (`shell/pressOrigin.ts`).
*/

import { useEffect } from 'react';

type PressType = { waiting: Array<(clicked: Element | null) => void> };

/** A release that no click follows (pointer let go outside the window) ends the press after this. */
const UNCLICKED_RELEASE_MS = 1000;

let press: PressType | null = null;
let unclicked: ReturnType<typeof setTimeout> | undefined;
/** Ended presses whose waiters are still to hear their click; a next press does not cancel them. */
const delivering = new Set<ReturnType<typeof setTimeout>>();
let installs = 0;

/** The press now down, any button: a new object per press, so a menu can name the one it opened in. */
let pressToken: object | null = null;
/**
 * The press released last. A press can end with no click at all (a right button's, which opens a
 * context menu; a long press the browser read as one): the next click is then another press's.
 */
let releasedToken: object | null = null;
let lastInput: InputKind | null = null;
/** When the last press was, in `Date.now()` time: a pointer down (any button), or Enter or Space. */
let lastPressAt: number | null = null;
/** Handlers for the next click, run in the capture phase before anything else on the page hears it. */
const beforeClick = new Set<(event: MouseEvent) => void>();

export type InputKind = 'touch' | 'mouse' | 'keyboard';

function settle(clicked: Element | null): void {
  const ended = press;
  press = null;
  clearTimeout(unclicked);
  if (!ended || ended.waiting.length === 0) return;
  // After the click's own handlers (React's sit at the root), so what the click did comes first.
  const delivery = setTimeout(() => {
    delivering.delete(delivery);
    for (const resolve of ended.waiting) resolve(clicked);
  }, 0);
  delivering.add(delivery);
}

function onPointerDown(event: PointerEvent): void {
  // What a menu reads: every press counts, a right button's included.
  lastInput = event.pointerType === 'mouse' ? 'mouse' : 'touch';
  lastPressAt = Date.now();
  pressToken = {};
  // What a blur waits on: the primary button or a touch; a right-click opens a menu and clicks nothing.
  if (event.button > 0) return;
  if (press) settle(null);
  press = { waiting: [] };
}

function onPointerUp(): void {
  if (pressToken) releasedToken = pressToken;
  pressToken = null;
  if (!press) return;
  clearTimeout(unclicked);
  unclicked = setTimeout(() => settle(null), UNCLICKED_RELEASE_MS);
}

function onClick(event: MouseEvent): void {
  for (const handler of [...beforeClick]) handler(event);
  settle(event.target instanceof Element ? event.target : null);
}

function onPointerCancel(): void {
  if (pressToken) releasedToken = pressToken;
  pressToken = null;
  settle(null);
}

function onKeyDown(event: KeyboardEvent): void {
  lastInput = 'keyboard';
  // A key that presses what has focus, as a click would.
  if (event.key === 'Enter' || event.key === ' ') lastPressAt = Date.now();
}

// From the first import, never while something is mounted: the press that opens the first menu has
// already begun by the time its component renders.
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointerup', onPointerUp, true);
  document.addEventListener('pointercancel', onPointerCancel, true);
  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKeyDown, true);
}

/**
 * Watches presses for the blurs that wait on them. The tracker itself runs from module load; what a
 * watcher holds is the right to be told, so when the last one goes the pending deliveries are dropped.
 */
export function trackPointerPresses(): () => void {
  installs++;
  return () => {
    if (--installs > 0) return;
    press = null;
    clearTimeout(unclicked);
    // Nothing that asked is mounted any more, so nothing is left to tell.
    for (const delivery of delivering) clearTimeout(delivery);
    delivering.clear();
  };
}

/** The press now down (any button), or null: compare with `pressReleasedAs` to know a press ended. */
export function pressNowDown(): object | null {
  return pressToken;
}

/** The press released last, or null before any. */
export function pressReleasedLast(): object | null {
  return releasedToken;
}

/** What reached the page last: a touch or pen, a mouse, or a key; null before any. */
export function lastInputKind(): InputKind | null {
  return lastInput;
}

/** When the last press was (a pointer down, or Enter or Space), or null before any. */
export function lastPressTime(): number | null {
  return lastPressAt;
}

/** Tests only: forget when the last press was. */
export function forgetLastPress(): void {
  lastPressAt = null;
}

/**
 * Runs `handler` for the next click, in the capture phase before anything else hears it, and drops it
 * after that one. Returns what removes it earlier.
 */
export function beforeNextClick(handler: (event: MouseEvent) => void): () => void {
  const once = (event: MouseEvent) => {
    beforeClick.delete(once);
    handler(event);
  };
  beforeClick.add(once);
  return () => {
    beforeClick.delete(once);
  };
}

/**
 * Inside a press, calls `then` with what its click landed on (null when no click came) once the click
 * has run, and returns true. Outside one, returns false and never calls it.
 */
export function whenPressEnds(then: (clicked: Element | null) => void): boolean {
  if (!press) return false;
  press.waiting.push(then);
  return true;
}

/** Watches presses while the component is mounted, for its `afterPress` blurs. */
export function usePointerPresses(): void {
  useEffect(() => trackPointerPresses(), []);
}

/**
 * Runs `then` at once, or, for a blur inside a press, once the press's click has run, with what it
 * clicked. The component calling it watches presses (`usePointerPresses`).
 */
export function afterPress(then: (clicked: Element | null) => void): void {
  if (!whenPressEnds(then)) then(null);
}
